import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getUserVeriffVerificationStatus,
  getUserVeriffVerificationStatusLightRegisteredUser,
} from '../../api/veriff/veriffVerification';
import { VerificationStatus } from '../../@types/VerificationStatus';
import {
  VeriffStatus,
  VeriffErrorCode,
  getErrorTranslationKey,
} from '../../api/veriff/model/UserVeriffVerificationStatus';
import VerificationStatusCheck from '../../components/verificationStatusCheck/VerificationStatusCheck';
import { VerificationMethod } from '../../@types/VerificationMethod';
import { trackEvent } from '../../tracking/tracking';
import { useClientConfig } from '../../utils/clientConfigContext';
import { VerificationCompletionData } from '../../@types/VerificationCompletionData';
import { useTvsState } from '../../utils/useTvsState';

const POLLING_INTERVAL_MS = 1000;
const PROGRESS_INCREMENT = 20;
const MAX_PROGRESS = 95;
const COMPLETION_DELAY_MS = 1000;
const MAX_VERIFICATION_TIME_MS = 15000;
const CLOSE_BUTTON_DELAY_MS = 5000;
const PROGRESS_COMPLETE_TIME_MS = 14000;

interface VeriffStatusCheckPageProps {
  onVerificationComplete?: (status: VerificationStatus, data: VerificationCompletionData) => void;
  onError: (error: string) => void;
}

const VeriffStatusCheckPage: React.FC<VeriffStatusCheckPageProps> = ({ onVerificationComplete, onError }) => {
  const [progress, setProgress] = useState(0);
  const [isChecking, setIsChecking] = useState(true);
  const [searchParams] = useSearchParams();
  const [, setTvsState] = useTvsState();
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [showFullProgress, setShowFullProgress] = useState(false);
  const { clientId, hideToasterOnSuccess, userId } = useClientConfig();

  const sessionId = searchParams.get('tvs_session_id');

  const handleVerificationSuccess = useCallback(() => {
    setShowFullProgress(true);
    setProgress(100);
    setTimeout(() => {
      setIsChecking(false);
      trackEvent('is24.de.sicherheit.verifizierung.veriff', 'verification_successful', 'user', clientId, 'veriff');
      onVerificationComplete?.(VerificationStatus.SUCCESS, {
        sessionId: sessionId || '',
        verificationMethod: VerificationMethod.Veriff,
      });
      if (hideToasterOnSuccess) {
        setTvsState('');
      } else {
        setTvsState('verification_success');
      }
    }, COMPLETION_DELAY_MS);
  }, [onVerificationComplete, setTvsState, clientId, hideToasterOnSuccess, sessionId]);

  const handleVerificationFailure = (errorKey?: string) => {
    setShowFullProgress(true);
    setProgress(100);
    if (errorKey) {
      onError(errorKey);
    }
    setTimeout(() => {
      setIsChecking(false);
      onVerificationComplete?.(VerificationStatus.FAILED, {
        sessionId: sessionId || '',
        verificationMethod: VerificationMethod.Veriff,
      });
      setTvsState('verification_failure_veriff');
    }, COMPLETION_DELAY_MS);
  };

  const handleClose = useCallback(() => {
    setIsChecking(false);
    onVerificationComplete?.(VerificationStatus.FAILED, {
      sessionId: sessionId || '',
      verificationMethod: VerificationMethod.Veriff,
    });
    setTvsState('verification_failure_veriff');
  }, [onVerificationComplete, setTvsState, sessionId]);

  useEffect(() => {
    if (!sessionId && isChecking) {
      handleVerificationFailure();
    }
  }, [sessionId, isChecking]);

  const checkVeriffStatus = useCallback(async () => {
    if (!sessionId) return;

    try {
      const veriffResponse = userId
        ? await getUserVeriffVerificationStatusLightRegisteredUser(sessionId, userId)
        : await getUserVeriffVerificationStatus(sessionId);

      if (veriffResponse.status === VeriffStatus.VERIFIED && veriffResponse.verified) {
        handleVerificationSuccess();
      } else if (
        veriffResponse.status === VeriffStatus.EXPIRED ||
        veriffResponse.status === VeriffStatus.DECLINED ||
        veriffResponse.status === null ||
        veriffResponse.status === undefined ||
        veriffResponse.errorReason
      ) {
        const errorKey = getErrorTranslationKey(veriffResponse.errorReason as VeriffErrorCode);
        handleVerificationFailure(errorKey);
      }
    } catch (error) {
      console.error('Error checking Veriff status:', error);
    }
  }, [sessionId, userId, handleVerificationSuccess]);

  const updateProgress = useCallback(() => {
    setProgress((prevProgress) => {
      return Math.min(prevProgress + PROGRESS_INCREMENT, MAX_PROGRESS);
    });
  }, []);

  useEffect(() => {
    let verificationTimer: NodeJS.Timeout;
    let progressTimer: NodeJS.Timeout;
    let maxVerificationTimer: NodeJS.Timeout;
    let closeButtonTimer: NodeJS.Timeout;
    let progressCompleteTimer: NodeJS.Timeout;

    const cleanup = () => {
      clearInterval(progressTimer);
      clearInterval(verificationTimer);
      clearTimeout(maxVerificationTimer);
      clearTimeout(closeButtonTimer);
      clearTimeout(progressCompleteTimer);
    };

    if (isChecking) {
      maxVerificationTimer = setTimeout(() => {
        if (isChecking) {
          handleVerificationFailure();
        }
      }, MAX_VERIFICATION_TIME_MS);

      closeButtonTimer = setTimeout(() => {
        setShowCloseButton(true);
      }, CLOSE_BUTTON_DELAY_MS);

      progressCompleteTimer = setTimeout(() => {
        if (isChecking) {
          setShowFullProgress(true);
          setProgress(100);
        }
      }, PROGRESS_COMPLETE_TIME_MS);

      verificationTimer = setInterval(checkVeriffStatus, POLLING_INTERVAL_MS);
      progressTimer = setInterval(updateProgress, POLLING_INTERVAL_MS);

      return () => {
        cleanup();
      };
    }
  }, [isChecking, checkVeriffStatus, updateProgress, handleVerificationFailure]);

  if (!isChecking) return null;

  const finalProgress = showFullProgress ? 100 : progress;

  return (
    <div data-testid='verification-status-polling'>
      <VerificationStatusCheck
        progress={finalProgress}
        verificationMethod={VerificationMethod.Veriff}
        showCloseButton={showCloseButton}
        onClose={handleClose}
      />
    </div>
  );
};

export default VeriffStatusCheckPage;
